import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Tabella cercabile del mapping ATECO 2025 → Cluster VIVA ESG.
 * Include pulsante di download del file Excel completo (admin only).
 */
export default function AtecoMappingTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ateco-cluster-mapping");
        setRows(data.items || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento mapping");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      const hay = `${r.cluster_code} ${r.cluster_label} ${r.codice_ateco} ${r.descrizione_attivita}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [rows, q]);

  async function download() {
    setDownloading(true);
    try {
      const res = await api.get("/ateco-cluster-mapping.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mapping-ateco2025-cluster.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore download");
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;
  }

  return (
    <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden mt-4">
      <div className="p-5 border-b border-slate-100 flex items-center gap-4 flex-wrap">
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Mapping ATECO 2025 → Cluster settoriale</div>
          <div className="text-slate-500 text-xs mt-0.5">
            {rows.length.toLocaleString("it-IT")} codici ATECO mappati sui 14 cluster settoriali VIVA ESG. Ricerca per codice, cluster o descrizione.
          </div>
        </div>
        {isAdmin && (
          <Button
            type="button"
            variant="outline"
            onClick={download}
            disabled={downloading}
            className="ml-auto"
            data-testid="ateco-mapping-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Scarica Excel
          </Button>
        )}
      </div>

      <div className="p-5 border-b border-slate-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per codice ATECO, cluster o attività..."
            className="pl-9"
            data-testid="ateco-mapping-search-input"
          />
        </div>
        {q && (
          <div className="text-xs text-slate-500 mt-2">
            {filtered.length.toLocaleString("it-IT")} {filtered.length === 1 ? "risultato" : "risultati"} su {rows.length.toLocaleString("it-IT")}
          </div>
        )}
      </div>

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50/80 sticky top-0 z-10">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3 w-40">Cluster</th>
              <th className="px-6 py-3 w-32">Codice ATECO</th>
              <th className="px-6 py-3">Descrizione attività</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.slice(0, 500).map((r) => (
              <tr key={r.codice_ateco} className="hover:bg-slate-50/50" data-testid={`ateco-mapping-row-${r.codice_ateco}`}>
                <td className="px-6 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <Badge variant="outline" className="w-fit font-mono text-[10px]">{r.cluster_code}</Badge>
                    <span className="text-[11px] text-slate-500">{r.cluster_label}</span>
                  </div>
                </td>
                <td className="px-6 py-2.5 font-mono text-xs font-semibold text-slate-900">{r.codice_ateco}</td>
                <td className="px-6 py-2.5 text-slate-700">{r.descrizione_attivita}</td>
              </tr>
            ))}
            {filtered.length > 500 && (
              <tr>
                <td colSpan={3} className="px-6 py-3 text-xs text-slate-500 italic text-center bg-slate-50/50">
                  Mostrati i primi 500 risultati. Affina la ricerca o usa &quot;Scarica Excel&quot; per la lista completa.
                </td>
              </tr>
            )}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-6 py-8 text-sm text-slate-500 text-center">Nessun risultato per la ricerca.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
